import type * as maplibregl from "maplibre-gl";
import { PEAK_ICON_DARK_IMAGE_ID, POI_ICON_SVG_PATHS, poiIconImageId } from "./poiIcons";

// Тёмная тема карты. Светлый стиль (basemapLayers.ts) остаётся источником
// правды: тёмные значения - это переопределения paint-свойств слоёв, которые
// накладываются живьём (setPaintProperty), без пересоздания стиля. Исходное
// значение запоминается при первом наложении и возвращается обратно при
// переходе на светлую тему.
//
// Палитра - холодный графит с фирменным зелёным: фон и вода темнее суши,
// дороги и тропы светлее фона, подписи светлые с тёмным ореолом.

const BG = "#13171c";
const HALO = "#13171c";
const TEXT = "#d9dee5";

type Paint = Record<string, unknown>;

// layer id -> { paint property -> значение в тёмной теме }
const DARK: Record<string, Paint> = {
  "basemap-background": { "background-color": BG },
  "landcover-ground": {
    "fill-color": [
      "match", ["get", "kind"],
      "water", "#1c3a4a",
      "forest", "#1d3322",
      "scrub", "#25392a",
      "glacier", "#33444f",
      "scree", "#2d2b28",
      "blockfield", "#252320",
      "transparent",
    ],
  },
  "landcover-water": {
    "fill-color": [
      "match", ["get", "kind"],
      "water", "#1c3a4a",
      "forest", "#1d3322",
      "scrub", "#25392a",
      "glacier", "#33444f",
      "scree", "#2d2b28",
      "blockfield", "#252320",
      "transparent",
    ],
  },
  "landcover-ground-outline": { "line-color": "#47683f" },
  hillshade: {
    "hillshade-shadow-color": "rgba(0, 0, 0, 0.55)",
    "hillshade-highlight-color": "rgba(255, 255, 255, 0.10)",
    "hillshade-accent-color": "rgba(0, 0, 0, 0.30)",
  },
  "waterways-line": { "line-color": ["match", ["get", "waterway"], "river", "#3d88ab", "#2f6f8c"] },
  "waterways-line-intermittent": { "line-color": ["match", ["get", "waterway"], "river", "#3d88ab", "#2f6f8c"] },
  "contours-line": { "line-color": "#64503f" },
  "contours-index": { "line-color": "#80664c" },
  "contours-label": { "text-color": "#a38c76", "text-halo-color": HALO },
  "contours-slope-ticks": { "line-pattern": "contour-tick-dark" },
  "ridges-line": { "line-color": "#c58b63" },
  "buildings-fill": { "fill-color": "#444a52" },
  "buildings-dam": { "fill-color": "#5d656e" },
  "buildings-outline-dam": { "line-color": "#9aa3ab" },
  "buildings-outline": { "line-color": "#7b828a" },
  "roads-casing-track": { "line-color": "#0b0d10" },
  "roads-casing": { "line-color": "#0b0d10" },
  "roads-line": { "line-color": "#8e97a1" },
  "power-lines-line": { "line-color": "#6b7078" },
  "railways-line": { "line-color": "#c9ced4" },
  "railways-ticks": { "line-pattern": "railway-tick-dark" },
  "railways-tunnel": { "line-color": "#c9ced4" },
  "railways-unused": { "line-color": "#7b828a" },
  "railways-tram": { "line-color": "#9aa3ab" },
  "railway-stations-circle": { "circle-color": "#13171c", "circle-stroke-color": "#c9ced4" },
  "railway-stations-label": { "text-color": TEXT, "text-halo-color": HALO },
  "trails-line": { "line-color": "#eceff3" },
  "pois-peak": { "text-color": TEXT, "text-halo-color": HALO },
  "pois-pass": { "text-color": TEXT, "text-halo-color": HALO, "icon-color": "#e7e2d9" },
  "pois-peak-admin": { "text-color": TEXT, "text-halo-color": HALO },
  "pois-pass-admin": { "text-color": TEXT, "text-halo-color": HALO, "icon-color": "#e7e2d9" },
  "pois-power-tower": { "icon-color": "#e7e2d9" },
  "pois-label": { "text-color": TEXT, "text-halo-color": HALO },
  "buildings-label": { "text-color": "#c9bfb2", "text-halo-color": HALO },
  "buildings-label-admin": { "text-color": "#c9bfb2", "text-halo-color": HALO },
  "waterways-label": { "text-halo-color": HALO },
  "landcover-water-label": { "text-halo-color": HALO },
  "places-label": { "text-color": "#e3e1dc", "text-halo-color": HALO },
  "boundaries-label": { "text-color": "#ff9a8f", "text-halo-color": HALO },
};

// То же для layout-свойств (иконка вершины - растровая, перекрасить нельзя,
// поэтому в тёмной теме подставляется её тёмный вариант).
const DARK_LAYOUT: Record<string, Paint> = {
  "pois-peak": { "icon-image": PEAK_ICON_DARK_IMAGE_ID },
  "pois-peak-admin": { "icon-image": PEAK_ICON_DARK_IMAGE_ID },
};

// Стиль «Генштаб»: бумажная топографическая карта советского образца
// (Генштаб ВС СССР, масштабы 1:50 000 - 1:200 000). Тот же механизм, что у
// тёмной темы: переопределения paint-свойств поверх светлого стиля, без
// пересоздания слоёв. Ориентир - палитра листов серии К-38 (Армения,
// Ереван-Севан): белая бумага, оранжево-коричневые горизонтали и дороги,
// голубая вода, оливковая растительность, чёрные здания и подписи, синие
// подписи гидрографии.
const GS_PAPER = "#fbf8f0";
const GS_INK = "#1c1a18";
// Горизонтали на листах - красно-оранжевые (с. 33 легенды).
const GS_BROWN = "#d4552d";
const GS_WATER = "#2b7fc2";

const GENSHTAB: Record<string, Paint> = {
  "basemap-background": { "background-color": GS_PAPER },
  "landcover-ground": {
    "fill-color": [
      "match", ["get", "kind"],
      "water", "#9ccbea",
      "forest", "#cdd88f",
      "scrub", "#e6ebc0",
      "glacier", "#e9f2f8",
      "scree", "#efe0c6",
      "blockfield", "#e2cba8",
      "transparent",
    ],
    // Часть тона отдана раскраске высот под покровом (gs-hypsometric).
    "fill-opacity": 0.6,
  },
  "landcover-water": {
    "fill-color": [
      "match", ["get", "kind"],
      "water", "#9ccbea",
      "forest", "#cdd88f",
      "scrub", "#e6ebc0",
      "glacier", "#e9f2f8",
      "scree", "#efe0c6",
      "blockfield", "#e2cba8",
      "transparent",
    ],
  },
  "landcover-ground-outline": { "line-color": "#8c9a4a" },
  hillshade: {
    // Плотность тени зависит от масштаба листа: на обзорных (1:500 000 и
    // мельче) рельеф лепится тенью, на 1:100 000 и крупнее - почти одними
    // горизонталями (листы 1:25 000-1:100 000 тени не имеют вовсе).
    "hillshade-exaggeration": ["interpolate", ["linear"], ["zoom"], 8, 0.75, 11, 0.55, 13, 0.3, 15, 0.14, 17, 0.06],
    "hillshade-shadow-color": "rgba(120, 70, 30, 0.45)",
    "hillshade-highlight-color": "rgba(255, 250, 235, 0.55)",
    "hillshade-accent-color": "rgba(150, 90, 40, 0.20)",
  },
  "waterways-line": { "line-color": GS_WATER },
  "waterways-line-intermittent": { "line-color": GS_WATER },
  "contours-line": { "line-color": GS_BROWN },
  "contours-index": { "line-color": "#b8421c" },
  "contours-label": { "text-color": "#b8421c", "text-halo-color": GS_PAPER },
  "contours-slope-ticks": { "line-pattern": "contour-tick-gs" },
  "cliffs-line": { "line-pattern": "gs-cliff-hachure" },
  "ridges-line": { "line-color": "#8a3d12" },
  // Красная заливка кварталов (с. 6 легенды) отменена: здания - чёрные, как
  // отдельные строения на с. 9.
  "buildings-fill": { "fill-color": "#2b2523" },
  "buildings-dam": { "fill-color": "#2b2523" },
  "buildings-outline-dam": { "line-color": GS_INK },
  "buildings-outline": { "line-color": GS_INK },
  // Там же, с. 21: шоссе и автострады - чёрные кромки с оранжево-красной
  // заливкой, улучшенные грунтовые - те же кромки с белой (пустой) заливкой.
  "roads-casing-track": { "line-color": GS_INK },
  "roads-casing": { "line-color": GS_INK },
  "roads-line": {
    "line-color": [
      "match", ["get", "highway"],
      ["motorway", "trunk", "primary", "secondary", "tertiary"], "#e2622b",
      "#ffffff",
    ],
  },
  "power-lines-line": { "line-color": GS_INK },
  "railways-line": { "line-color": GS_INK },
  "railways-tunnel": { "line-color": GS_INK },
  "railway-stations-circle": { "circle-color": GS_PAPER, "circle-stroke-color": GS_INK },
  "railway-stations-label": { "text-color": GS_INK, "text-halo-color": GS_PAPER },
  "trails-line": { "line-color": GS_INK },
  "pois-peak": { "text-color": GS_INK, "text-halo-color": GS_PAPER },
  "pois-pass": { "text-color": GS_INK, "text-halo-color": GS_PAPER },
  "pois-peak-admin": { "text-color": GS_INK, "text-halo-color": GS_PAPER },
  "pois-pass-admin": { "text-color": GS_INK, "text-halo-color": GS_PAPER },
  "pois-label": { "text-color": GS_INK, "text-halo-color": GS_PAPER },
  "buildings-label": { "text-color": GS_INK, "text-halo-color": GS_PAPER },
  "buildings-label-admin": { "text-color": GS_INK, "text-halo-color": GS_PAPER },
  "waterways-label": { "text-color": GS_WATER, "text-halo-color": GS_PAPER },
  "landcover-water-label": { "text-color": GS_WATER, "text-halo-color": GS_PAPER },
  "places-label": { "text-color": GS_INK, "text-halo-color": GS_PAPER },
  "pois-icon-unclustered": { "icon-color": GS_INK },
  // Белый диск под значком с чёрным ободком не нужен на бумажной карте.
  "pois-icon-unclustered-halo": { "circle-color": GS_PAPER, "circle-stroke-color": GS_PAPER },
  "boundaries-label": { "text-color": "#8c1f14", "text-halo-color": GS_PAPER },
};

// Шрифты «Генштаба»: гидрография и высоты изолиний - курсивом с засечками,
// названия населённых пунктов остаются рубленым шрифтом (как на с. 44 легенды).
const GS_SERIF_ITALIC = ["Noto Serif Italic"];
// Значки точечных объектов «Генштаба»: где в легенде есть свой знак (вершина,
// родник, колодец, пещера, вышка) - он; остальные пиктограммы (скамейки,
// туалеты, хижины...) в легенде отсутствуют, поэтому остаются нашими
// однотонными глифами того же рисованного типа, перекрашенными в чёрный.
const GS_POI_OWN_ICON: Record<string, string> = {
  spring: "gs-spring",
  water_well: "gs-well",
  cave_entrance: "gs-cave",
  tower: "gs-tower",
};
const GS_POI_ICON_EXPR: any = [
  "match",
  ["get", "kind"],
  ...Object.keys(POI_ICON_SVG_PATHS).flatMap((kind) => [kind, GS_POI_OWN_ICON[kind] ?? poiIconImageId(kind)]),
  "",
];

const GENSHTAB_LAYOUT: Record<string, Paint> = {
  // Опоры ЛЭП (сотни тысяч точек) на этом стиле скрыты; сами линии остаются.
  "pois-power-tower": { visibility: "none" },
  "pois-icon-unclustered": { "icon-image": GS_POI_ICON_EXPR },
  "pois-peak": { "icon-image": "gs-peak" },
  "pois-peak-admin": { "icon-image": "gs-peak" },
  // Названия городов - прописными (с. 44), чем крупнее, тем разрежённее.
  "places-label": {
    "text-transform": ["match", ["get", "kind"], ["city", "town"], "uppercase", "none"],
    "text-letter-spacing": ["match", ["get", "kind"], "city", 0.12, "town", 0.06, 0],
    // До z12 рядом с названием стоит кружок-пунсон (gs-place-circle): подпись
    // сдвигается вбок от него, а не лежит поверх.
    "text-variable-anchor": ["left", "right", "top", "bottom"],
    "text-radial-offset": ["step", ["zoom"], 0.85, 12, 0],
    "text-justify": "auto",
  },
  "waterways-label": { "text-font": GS_SERIF_ITALIC },
  "landcover-water-label": { "text-font": GS_SERIF_ITALIC },
  "contours-label": { "text-font": GS_SERIF_ITALIC },
};

// layer id -> property -> исходное (светлое) значение
const originals = new WeakMap<maplibregl.Map, Map<string, unknown>>();

const keyOf = (layer: string, prop: string) => `${layer}\u0000${prop}`;

export type MapStyleName = "default" | "genshtab";

// Целевое значение свойства для выбранного режима: у «Генштаба» своя палитра,
// тёмная тема на него не накладывается (бумажная карта - всегда светлая).
const targetTables = (dark: boolean, style: MapStyleName) =>
  style === "genshtab" ? { paint: GENSHTAB, layout: GENSHTAB_LAYOUT } : { paint: dark ? DARK : {}, layout: dark ? DARK_LAYOUT : {} };

export const applyMapTheme = (map: maplibregl.Map, dark: boolean, style: MapStyleName = "default"): void => {
  let saved = originals.get(map);
  if (!saved) {
    saved = new Map();
    originals.set(map, saved);
  }
  const target = targetTables(dark, style);
  // Обходим объединение всех таблиц: свойство, которого нет в выбранном
  // режиме, возвращается к исходному значению.
  const layers = new Set([...Object.keys(DARK), ...Object.keys(GENSHTAB)]);
  for (const layer of layers) {
    if (!map.getLayer(layer)) continue;
    const props = new Set([...Object.keys(DARK[layer] ?? {}), ...Object.keys(GENSHTAB[layer] ?? {})]);
    for (const prop of props) {
      const k = keyOf(layer, prop);
      if (!saved.has(k)) saved.set(k, map.getPaintProperty(layer, prop as never));
      const value = target.paint[layer]?.[prop];
      // setPaintProperty(undefined) возвращает свойство к значению по умолчанию.
      map.setPaintProperty(layer, prop as never, (value !== undefined ? value : saved.get(k)) as never);
    }
  }
  for (const layer of new Set([...Object.keys(DARK_LAYOUT), ...Object.keys(GENSHTAB_LAYOUT)])) {
    if (!map.getLayer(layer)) continue;
    for (const prop of new Set([...Object.keys(DARK_LAYOUT[layer] ?? {}), ...Object.keys(GENSHTAB_LAYOUT[layer] ?? {})])) {
      const k = `layout\u0000${keyOf(layer, prop)}`;
      if (!saved.has(k)) saved.set(k, map.getLayoutProperty(layer, prop as never));
      const value = target.layout[layer]?.[prop];
      map.setLayoutProperty(layer, prop as never, (value !== undefined ? value : saved.get(k)) as never);
    }
  }
};
